// src/ui/screens/Compare.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listRecentSessions, db, type Session, type ResultSummary } from "../../storage/db";
import { colors } from "../design-system/colors";
import { fontFamily, typeScale } from "../design-system/typography";
import { space, radius, minTapTarget } from "../design-system/spacing";

interface Entry {
  session: Session;
  summary: ResultSummary | undefined;
}

export default function Compare() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const sessions = await listRecentSessions(20);
      const withSummaries = await Promise.all(
        sessions.map(async (session) => ({
          session,
          summary: await db.resultSummaries.get(session.id),
        })),
      );
      setEntries(withSummaries);
      if (withSummaries.length >= 2) {
        setLeftId(withSummaries[1].session.id);
        setRightId(withSummaries[0].session.id);
      }
    })();
  }, []);

  const left = entries.find((e) => e.session.id === leftId);
  const right = entries.find((e) => e.session.id === rightId);

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/history")} style={styles.backButton}>
        ← 戻る
      </button>
      <h1 style={styles.title}>フォーム比較</h1>

      {entries.length < 2 ? (
        <p style={styles.empty}>比較するには、あと{2 - entries.length}回計測が必要です。</p>
      ) : (
        <>
          <div style={styles.pickerRow}>
            <SessionPicker label="以前" entries={entries} value={leftId} onChange={setLeftId} />
            <SessionPicker label="今回" entries={entries} value={rightId} onChange={setRightId} />
          </div>

          <div style={styles.metricsGrid}>
            <MetricRow label="総合スコア" left={left?.summary?.overallScore} right={right?.summary?.overallScore} unit="" />
            <MetricRow
              label="最大圧縮力"
              left={left?.summary?.maxCompressionForce}
              right={right?.summary?.maxCompressionForce}
              unit="N"
            />
            <MetricRow
              label="最大せん断力"
              left={left?.summary?.maxShearForce}
              right={right?.summary?.maxShearForce}
              unit="N"
            />
          </div>
        </>
      )}
    </div>
  );
}

function SessionPicker({
  label,
  entries,
  value,
  onChange,
}: {
  label: string;
  entries: Entry[];
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <div style={{ flex: 1 }}>
      <div style={styles.pickerLabel}>{label}</div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        style={styles.select}
      >
        {entries.map((e) => (
          <option key={e.session.id} value={e.session.id}>
            {new Date(e.session.startedAt).toLocaleDateString("ja-JP")}
          </option>
        ))}
      </select>
    </div>
  );
}

function MetricRow({
  label,
  left,
  right,
  unit,
}: {
  label: string;
  left: number | undefined;
  right: number | undefined;
  unit: string;
}) {
  const improved = left !== undefined && right !== undefined && right < left;
  return (
    <div style={styles.metricRow}>
      <span style={styles.metricValue}>
        {left !== undefined ? `${left.toFixed(0)}${unit}` : "-"}
      </span>
      <span style={styles.metricLabel}>{label}</span>
      <span
        style={{
          ...styles.metricValue,
          color: improved ? colors.safe : colors.textPrimary,
        }}
      >
        {right !== undefined ? `${right.toFixed(0)}${unit}` : "-"}
      </span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100dvh",
    background: colors.bg,
    color: colors.textPrimary,
    fontFamily: fontFamily.ui,
    padding: space.lg,
    maxWidth: 480,
    margin: "0 auto",
  },
  backButton: {
    background: "none",
    border: "none",
    color: colors.textSecondary,
    fontFamily: fontFamily.ui,
    fontSize: typeScale.body.size,
    padding: 0,
    marginBottom: space.md,
    minHeight: minTapTarget,
    cursor: "pointer",
  },
  title: { fontSize: typeScale.h1.size, marginBottom: space.lg },
  empty: { color: colors.textSecondary },
  pickerRow: { display: "flex", gap: space.md, marginBottom: space.lg },
  pickerLabel: { fontSize: typeScale.caption.size, color: colors.textSecondary, marginBottom: space.xs },
  select: {
    width: "100%",
    minHeight: minTapTarget,
    borderRadius: radius.md,
    border: `1px solid ${colors.surfaceBorder}`,
    background: colors.surface,
    color: colors.textPrimary,
    fontFamily: fontFamily.ui,
    padding: `0 ${space.sm}`,
  },
  metricsGrid: { display: "flex", flexDirection: "column", gap: space.sm },
  metricRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    padding: space.md,
    borderRadius: radius.md,
    background: colors.surface,
    border: `1px solid ${colors.surfaceBorder}`,
  },
  metricValue: {
    fontFamily: fontFamily.numeric,
    fontSize: typeScale.h2.size,
    fontWeight: 700,
    textAlign: "center",
  },
  metricLabel: {
    fontSize: typeScale.caption.size,
    color: colors.textSecondary,
    textAlign: "center",
    padding: `0 ${space.sm}`,
  },
};
